import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/components/ui/dropdown-menu';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';
import { ShieldAlert, Share2, Copy, MoreHorizontal } from 'lucide-react';
import { useAuthorizedAppsQuery, useRevokeAuthorizedApp } from '../hooks/useAuthorizedApps';
import { useToast } from '@/shared/hooks/use-toast';
import { logger } from '@/infrastructure/lib/logger';
import type { AuthorizedApp } from '../services/authorizedAppsService';

interface ProductAuthorizedAppsTabProps {
  workspaceId: string;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MCP_SCOPES = ['mcp:read', 'mcp:write', 'mcp:execute', 'mcp:admin'];
const API_SCOPES = ['api:read', 'api:write', 'api:execute', 'api:admin'];

export function WorkspaceAuthorizedAppsTab({ workspaceId }: ProductAuthorizedAppsTabProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const appsQuery = useAuthorizedAppsQuery(workspaceId);
  const revokeMutation = useRevokeAuthorizedApp(workspaceId);
  const [revokeTarget, setRevokeTarget] = useState<AuthorizedApp | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Tick every 30s so the On/Off status re-evaluates against the current time
  // without waiting for a full refetch or manual refresh.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const apps = appsQuery.data || [];

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const mcpUrl = `${origin}/mcp`;
  const apiUrl = `${origin}/api`;

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    await revokeMutation.mutateAsync(revokeTarget.id);
    setRevokeTarget(null);
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: t('workspaces.authorizedApps.share.urlCopied') });
    } catch (error: unknown) {
      const err = error as Error;
      logger.error('Failed to copy connection URL', { error: err?.message });
      toast({ variant: 'destructive', title: t('workspaces.authorizedApps.share.copyFailed') });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>{t('workspaces.authorizedApps.title')}</CardTitle>
              <CardDescription>{t('workspaces.authorizedApps.description')}</CardDescription>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShareOpen(true)}
              className="flex-shrink-0"
              aria-label={t('workspaces.authorizedApps.share.button')}
              title={t('workspaces.authorizedApps.share.button')}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {appsQuery.isLoading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <LoadingSpinner className="h-5 w-5" />
            </div>
          ) : apps.length === 0 ? (
            <div className="text-center py-8 border rounded-lg">
              <ShieldAlert className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-lg font-semibold mb-2">{t('workspaces.authorizedApps.empty')}</p>
              <p className="text-sm text-muted-foreground">{t('workspaces.authorizedApps.emptyDesc')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('workspaces.authorizedApps.columns.app')}</TableHead>
                  <TableHead className="w-[100px]">{t('workspaces.authorizedApps.columns.status')}</TableHead>
                  <TableHead>{t('workspaces.authorizedApps.columns.authorizedBy')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('workspaces.authorizedApps.columns.lastUsed')}</TableHead>
                  <TableHead className="w-[40px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => {
                  const isOn = new Date(app.expires_at).getTime() > Date.now();
                  return (
                    <TableRow key={app.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {app.logo_uri && <AvatarImage src={app.logo_uri} alt={app.client_name} />}
                            <AvatarFallback>{app.client_name.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{app.client_name}</div>
                            {app.client_uri && (
                              <a
                                href={app.client_uri}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-muted-foreground hover:underline truncate block"
                              >
                                {new URL(app.client_uri).hostname}
                              </a>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-flex items-center gap-1.5 text-xs"
                          title={t(isOn ? 'products.authorizedApps.status.onTooltip' : 'products.authorizedApps.status.offTooltip')}
                        >
                          <span className={`inline-block h-2 w-2 rounded-full ${isOn ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                          <span className={isOn ? 'font-medium' : 'text-muted-foreground'}>
                            {t(isOn ? 'products.authorizedApps.status.on' : 'products.authorizedApps.status.off')}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {app.authorized_by_name || app.authorized_by_email || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {app.last_used_at ? formatDate(app.last_used_at) : t('workspaces.authorizedApps.neverUsed')}
                        {app.usage_count > 0 && (
                          <span className="ml-1 text-xs opacity-70">· {app.usage_count}×</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setRevokeTarget(app)}
                              className="text-destructive focus:text-destructive"
                              disabled={revokeMutation.isPending}
                            >
                              {t('workspaces.authorizedApps.revoke')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaces.authorizedApps.revokeDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('workspaces.authorizedApps.revokeDialog.description', { appName: revokeTarget?.client_name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {t('workspaces.authorizedApps.revoke')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('workspaces.authorizedApps.share.title')}</DialogTitle>
            <DialogDescription>{t('workspaces.authorizedApps.share.description')}</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="api" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="api">{t('workspaces.authorizedApps.share.apiTitle')}</TabsTrigger>
              <TabsTrigger value="mcp">{t('workspaces.authorizedApps.share.mcpTitle')}</TabsTrigger>
            </TabsList>
            <TabsContent value="api" className="mt-4">
              <ShareResource
                description={t('workspaces.authorizedApps.share.apiDescription')}
                url={apiUrl}
                scopes={API_SCOPES}
                urlLabel={t('workspaces.authorizedApps.share.urlLabel')}
                scopesLabel={t('workspaces.authorizedApps.share.scopesLabel')}
                copyLabel={t('workspaces.authorizedApps.share.copyUrl')}
                onCopy={handleCopy}
                t={t}
              />
            </TabsContent>
            <TabsContent value="mcp" className="mt-4">
              <ShareResource
                description={t('workspaces.authorizedApps.share.mcpDescription')}
                url={mcpUrl}
                scopes={MCP_SCOPES}
                urlLabel={t('workspaces.authorizedApps.share.urlLabel')}
                scopesLabel={t('workspaces.authorizedApps.share.scopesLabel')}
                copyLabel={t('workspaces.authorizedApps.share.copyUrl')}
                onCopy={handleCopy}
                t={t}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ShareResourceProps {
  description: string;
  url: string;
  scopes: string[];
  urlLabel: string;
  scopesLabel: string;
  copyLabel: string;
  onCopy: (value: string) => void;
  t: TFunction;
}

function ShareResource({ description, url, scopes, urlLabel, scopesLabel, copyLabel, onCopy, t }: ShareResourceProps) {
  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="text-xs text-muted-foreground">{description}</div>

      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">{urlLabel}</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-muted px-2 py-1.5 rounded border font-mono truncate">
            {url}
          </code>
          <Button variant="outline" size="sm" onClick={() => onCopy(url)} aria-label={copyLabel}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">{scopesLabel}</div>
        <div className="flex flex-wrap gap-1">
          {scopes.map((s) => (
            <Badge
              key={s}
              variant={s.endsWith(':admin') ? 'destructive' : 'outline'}
              className="text-xs"
            >
              {t(`workspaces.authorizedApps.scopeLabels.${s.replace(':', '_')}`, s)}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
